import { useCallback, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
} from "ra-core";

import type {
  Company,
  Contact,
  Project,
  ProjectImportRow,
  ProjectImportSummary,
} from "../types";
import {
  buildProjectImportPreview,
  getProjectImportBatchId,
  parseProjectCsv,
  PROJECT_CSV_OPTIONAL_HEADERS,
  PROJECT_CSV_HEADERS,
  type ProjectCsvParseResult,
} from "../projects/projectCsvImport";
import { commitProjectImport } from "../projects/projectCsvCommit";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type ImportState =
  | { status: "idle" }
  | { status: "loading"; message: string }
  | {
      status: "ready";
      fileName: string;
      csv: string;
      parseResult: ProjectCsvParseResult;
      rows: ProjectImportRow[];
      summary: ProjectImportSummary;
    }
  | { status: "committing"; message: string }
  | {
      status: "success";
      fileName: string;
      summary: ProjectImportSummary;
      rows: ProjectImportRow[];
      imported: number;
      skipped: number;
      failed: Array<{ rowNumber: number; message: string }>;
    }
  | { status: "error"; message: string };

export const ProjectCsvImport = () => {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const [state, setState] = useState<ImportState>({ status: "idle" });

  const reset = useCallback(() => setState({ status: "idle" }), []);

  const handleFile = useCallback(
    async (file: File) => {
      setState({ status: "loading", message: "Reading and validating CSV…" });
      try {
        const csv = await file.text();
        const parseResult = parseProjectCsv(csv);
        if (parseResult.missingHeaders.length || parseResult.errors.length) {
          const details = [
            parseResult.missingHeaders.length
              ? `Missing headers: ${parseResult.missingHeaders.join(", ")}`
              : null,
            ...parseResult.errors.map((error) =>
              error.row == null
                ? error.message
                : `Row ${error.row + 1}: ${error.message}`,
            ),
          ].filter(Boolean);
          setState({ status: "error", message: details.join("\n") });
          return;
        }

        const batchId = getProjectImportBatchId(file.name, csv);
        const [contactsResponse, companiesResponse, projectsResponse] =
          await Promise.all([
            dataProvider.getList<Contact>("contacts", {
              pagination: { page: 1, perPage: 10_000 },
              sort: { field: "id", order: "ASC" },
            }),
            dataProvider.getList<Company>("companies", {
              pagination: { page: 1, perPage: 10_000 },
              sort: { field: "id", order: "ASC" },
            }),
            dataProvider.getList<Project>("projects", {
              pagination: { page: 1, perPage: 10_000 },
              sort: { field: "id", order: "ASC" },
            }),
          ]);
        const preview = buildProjectImportPreview(
          parseResult.data,
          contactsResponse.data,
          companiesResponse.data,
          { importBatchId: batchId, existingProjects: projectsResponse.data },
        );
        setState({
          status: "ready",
          fileName: file.name,
          csv,
          parseResult,
          ...preview,
        });
      } catch (error) {
        setState({
          status: "error",
          message:
            error instanceof Error ? error.message : "Unable to read this CSV.",
        });
      }
    },
    [dataProvider],
  );

  const commit = useCallback(async () => {
    if (state.status !== "ready" || !identity) {
      setState({
        status: "error",
        message: "You must be signed in to commit an import.",
      });
      return;
    }
    const batchId =
      state.summary.importBatchId ??
      getProjectImportBatchId(state.fileName, state.csv);
    setState({
      status: "committing",
      message: "Creating Projects, contacts, and notes…",
    });
    try {
      const result = await commitProjectImport({
        dataProvider,
        rows: state.rows,
        batchId,
        fileName: state.fileName,
        salesId: identity.id,
      });
      notify(
        result.failed.length
          ? "Import completed with review items."
          : "Project import completed.",
        { type: result.failed.length ? "warning" : "info" },
      );
      setState({
        status: "success",
        fileName: state.fileName,
        summary: state.summary,
        rows: state.rows,
        ...result,
      });
      refresh();
    } catch (error) {
      setState({
        status: "error",
        message:
          error instanceof Error
            ? error.message
            : "Unable to commit this import.",
      });
    }
  }, [dataProvider, identity, notify, refresh, state]);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Import Precision pipeline CSV</CardTitle>
        <p className="text-sm text-muted-foreground">
          Every source row becomes a Project. Contacts are reused only on
          confident matches; Companies are optional referral or partner links.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.status === "idle" || state.status === "error" ? (
          <div className="space-y-3">
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <DownloadTemplateButton />
            <p className="text-xs text-muted-foreground">
              Required headers are validated before any data is written.
              Optional header: Referral/Partner Company.
            </p>
            {state.status === "error" ? (
              <Alert variant="destructive">
                <AlertTitle>CSV import needs attention</AlertTitle>
                <AlertDescription className="whitespace-pre-line">
                  {state.message}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : null}

        {state.status === "loading" || state.status === "committing" ? (
          <p className="text-sm text-muted-foreground">{state.message}</p>
        ) : null}

        {state.status === "ready" ? (
          <>
            <ImportSummary summary={state.summary} />
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void commit()}>Commit import</Button>
              <Button variant="outline" onClick={reset}>
                Start over
              </Button>
              <DownloadReportButton
                fileName={state.fileName}
                summary={state.summary}
                rows={state.rows}
              />
            </div>
            <PreviewTable rows={state.rows} />
          </>
        ) : null}

        {state.status === "success" ? (
          <>
            <Alert>
              <AlertTitle>Project import complete</AlertTitle>
              <AlertDescription>
                Imported {state.imported} Projects, skipped {state.skipped}{" "}
                already-imported rows, and recorded {state.failed.length} row
                errors.
              </AlertDescription>
            </Alert>
            <DownloadReportButton
              fileName={state.fileName}
              summary={state.summary}
              rows={state.rows}
              failed={state.failed}
            />
            <Button variant="outline" onClick={reset}>
              Import another file
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

const ImportSummary = ({ summary }: { summary: ProjectImportSummary }) => (
  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
    <SummaryStat label="Source rows" value={summary.rowCount} />
    <SummaryStat label="Projects ready" value={summary.validRows} />
    <SummaryStat label="Errors" value={summary.errorRows} />
    <SummaryStat label="Warnings" value={summary.warningRows} />
    <SummaryStat label="Duplicate flags" value={summary.duplicateRows} />
    <SummaryStat label="Contact matches" value={summary.contactMatches} />
    <SummaryStat label="New contacts" value={summary.newContacts} />
    <SummaryStat label="Company matches" value={summary.companyMatches} />
    <SummaryStat label="Unmatched companies" value={summary.companyUnmatched} />
    <div className="col-span-full rounded-md border p-3">
      <p className="mb-2 font-medium">Projects by stage</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        {Object.entries(summary.byStage).map(([stage, count]) => (
          <span key={stage}>
            {stage}: {count}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const SummaryStat = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-md border p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-xl font-semibold">{value}</p>
  </div>
);

const DownloadTemplateButton = () => (
  <Button
    variant="link"
    className="px-0"
    onClick={() => {
      const headers = [...PROJECT_CSV_HEADERS, ...PROJECT_CSV_OPTIONAL_HEADERS];
      const blob = new Blob([`${headers.join(",")}\n`], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "precision-project-pipeline-template.csv";
      link.click();
      URL.revokeObjectURL(url);
    }}
  >
    Download CSV template with Referral/Partner Company column
  </Button>
);

const PreviewTable = ({ rows }: { rows: ProjectImportRow[] }) => (
  <div className="overflow-x-auto rounded-md border">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Row</TableHead>
          <TableHead>Customer / Project</TableHead>
          <TableHead>Source status</TableHead>
          <TableHead>Stage</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Company</TableHead>
          <TableHead>Review</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.slice(0, 20).map((row) => (
          <TableRow key={row.rowNumber}>
            <TableCell>{row.rowNumber}</TableCell>
            <TableCell className="max-w-56 truncate">
              {row.project.name}
            </TableCell>
            <TableCell>{row.project.source_status || "—"}</TableCell>
            <TableCell>{row.project.stage}</TableCell>
            <TableCell>{row.contactMatch ?? "none"}</TableCell>
            <TableCell>{row.companyName || "—"}</TableCell>
            <TableCell>
              {row.issues.length ? `${row.issues.length} issue(s)` : "Ready"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {rows.length > 20 ? (
      <p className="p-3 text-xs text-muted-foreground">
        Showing the first 20 of {rows.length} rows. The commit includes all
        rows.
      </p>
    ) : null}
  </div>
);

const DownloadReportButton = ({
  fileName,
  summary,
  rows,
  failed = [],
}: {
  fileName: string;
  summary: ProjectImportSummary;
  rows: ProjectImportRow[];
  failed?: Array<{ rowNumber: number; message: string }>;
}) => (
  <Button
    variant="outline"
    onClick={() => {
      const report = {
        fileName,
        expectedHeaders: [
          ...PROJECT_CSV_HEADERS,
          ...PROJECT_CSV_OPTIONAL_HEADERS,
        ],
        summary,
        rows: rows.map((row) => ({
          rowNumber: row.rowNumber,
          sourceKey: row.project.source_key,
          stage: row.project.stage,
          customer: row.project.name,
          companyName: row.companyName,
          rawRow: row.raw,
          issues: row.issues,
        })),
        failed,
      };
      const blob = new Blob([JSON.stringify(report, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName.replace(/\.csv$/i, "")}-project-import-report.json`;
      link.click();
      URL.revokeObjectURL(url);
    }}
  >
    Download review report
  </Button>
);

export default ProjectCsvImport;
